import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, Plus, TicketCheck, UserPlus, Users, UserX } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { FilterNode, ListResponse, SortSpec, TicketSummary } from '@aurora/contracts';
import { get, patch, post, type ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { absoluteTime, pluralise, relativeTime } from '../lib/format';
import { useSession } from '../lib/session';
import { Avatar, Badge, Button, Card, EmptyState, ErrorState, Skeleton } from '../components/ui';
import { useToast } from '../components/toast';
import { PageHeader } from './shell';
import { RequesterHome } from './requester-home';
import { invalidateTicket, ticketKeys } from '../features/tickets/list-cache';
import { OPEN_CATEGORIES, SLA_URGENCY_SORT, serialiseSort } from '../features/tickets/list-filters';
import { isRequesterView, seesOnlyAssigned } from '../features/tickets/list-scope';
import { SlaDeadline } from '../features/tickets/sla-deadline';
import { ticketStatusTone } from '../features/tickets/status-tone';
import { PriorityPill } from '../features/tickets/ticket-table';

/**
 * The dashboard.
 *
 * Deliberately answers one question — *what needs me next?* — rather than
 * presenting a wall of charts. Every number is a link into the ticket list with
 * the matching filter already applied, so a number is never a dead end, and
 * every count comes from the same search, under the same permission, as the
 * list it opens — so the two can never disagree.
 *
 *   * My queue is in the order the work is due — breached first, then the
 *     nearest SLA deadline — with each row's priority, status and deadline on
 *     it. It used to be in order of last activity, with none of those, so the
 *     most urgent ticket could sit at the bottom looking like the rest.
 *   * Unassigned work can be taken from here in one click.
 *   * Everything refreshes itself every minute, and whenever a ticket is
 *     changed anywhere in the app: the counts used to be cached under keys of
 *     their own, and a ticket resolved on the list was still counted here.
 *   * Somebody who sees only their own assignments is not shown desk-wide
 *     tiles that could only ever read zero for them.
 */

/** How often a dashboard left open keeps itself current. */
const REFRESH_MS = 60_000;

const URGENCY = serialiseSort(SLA_URGENCY_SORT)!;

function and(...nodes: FilterNode[]): FilterNode {
  return { logic: 'and', nodes };
}

const MINE = and({ field: 'assignee_id', op: 'is', value: '@me' }, OPEN_CATEGORIES);
const UNASSIGNED = and({ field: 'assignee_id', op: 'is_empty' }, OPEN_CATEGORIES);
/** Breached or about to be, and still open: a breach is never cleared, so without the status it grows for ever. */
const URGENT = and({ field: 'sla_state', op: 'in', value: ['breached', 'at_risk'] }, OPEN_CATEGORIES);
const DUE_TODAY_CONDITION: FilterNode = { field: 'sla_due_at', op: 'is', value: { relative: 'today' } };
const DUE_TODAY = and(DUE_TODAY_CONDITION, OPEN_CATEGORIES);

interface Tile {
  id: string;
  label: string;
  /** What exactly is counted, for the tooltip. */
  description: string;
  icon: typeof TicketCheck;
  tone: 'neutral' | 'accent' | 'caution' | 'critical';
  filter: FilterNode;
  href: string;
  /** About the whole desk, so meaningless to somebody who sees only their own assignments. */
  deskWide?: boolean;
}

const TILES: Tile[] = [
  {
    id: 'mine',
    label: 'Assigned to me',
    description: 'Open and pending tickets assigned to you.',
    icon: TicketCheck,
    tone: 'accent',
    filter: MINE,
    href: `/tickets?quick=mine,open&sort=${URGENCY}`,
  },
  {
    id: 'unassigned',
    label: 'Unassigned',
    description: 'Open and pending tickets nobody has picked up.',
    icon: UserX,
    tone: 'caution',
    filter: UNASSIGNED,
    href: '/tickets?quick=unassigned,open&sort=created_at',
    deskWide: true,
  },
  {
    id: 'urgent',
    label: 'Breached or at risk',
    description: 'Open tickets that have missed an SLA target or are about to.',
    icon: AlertTriangle,
    tone: 'critical',
    filter: URGENT,
    href: `/tickets?quick=breached,at_risk&sort=${URGENCY}`,
  },
  {
    id: 'due-today',
    label: 'Due today',
    description: 'Open tickets with an SLA deadline before midnight.',
    icon: CalendarClock,
    tone: 'neutral',
    filter: DUE_TODAY,
    href: `/tickets?quick=open&filter=${encodeURIComponent(JSON.stringify(and(DUE_TODAY_CONDITION)))}&sort=${URGENCY}`,
  },
];

export function DashboardRoute() {
  const { session } = useSession();
  // Which page this is depends on who is looking. Rendering the agent's while
  // the session was still loading sent six desk-wide searches on behalf of a
  // requester before swapping to their page.
  if (!session) return null;
  // Somebody who is not on the desk — or sees only what they raised — has no
  // queue, no assignments and no SLA to watch; they get a page built around
  // what they came to do. The same rule as the ticket list's "My requests".
  if (isRequesterView(session)) return <RequesterHome />;
  return <AgentDashboard />;
}

function AgentDashboard() {
  const { session, can } = useSession();
  const firstName = session?.user.name.split(' ')[0] ?? 'there';
  const assignedOnly = seesOnlyAssigned(session);
  const tiles = assignedOnly ? TILES.filter((tile) => !tile.deskWide) : TILES;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={`${greeting()}, ${firstName}`}
        description={assignedOnly ? 'Everything assigned to you.' : 'Everything waiting on you, and on the desk.'}
        // No "New ticket" here: the top bar's is one line above, on every
        // screen, and two identical primary buttons stacked on one page read
        // as two different things.
      />

      <div className="scrollbar-slim min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-6xl space-y-4">
          <div className={cn('grid gap-3 sm:grid-cols-2', tiles.length === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3')}>
            {tiles.map((tile) => (
              <CountTile key={tile.id} tile={tile} />
            ))}
          </div>

          <div className={cn('grid gap-4', !assignedOnly && 'lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]')}>
            <MyQueue offerUnassigned={!assignedOnly} />
            {!assignedOnly && (
              <div className="min-w-0 space-y-4">
                <UnassignedPanel canAssign={can('ticket.assign')} />
                {can('agent.view_workload') && <TeamPanel />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

const TONES = {
  neutral: 'text-ink-muted',
  accent: 'text-accent',
  caution: 'text-caution',
  critical: 'text-critical',
};

function CountTile({ tile }: { tile: Tile }) {
  const query = useQuery<ListResponse<TicketSummary>, ApiError>({
    queryKey: ticketKeys.count(`dashboard-${tile.id}`, tile.filter),
    // per_page 1 because only the total is needed; the row is incidental.
    queryFn: () => post<ListResponse<TicketSummary>>('/tickets/search', { filter: tile.filter, per_page: 1 }),
    refetchInterval: REFRESH_MS,
  });
  const total = query.data?.meta.total;

  return (
    <Link
      to={tile.href}
      title={tile.description}
      aria-label={`${tile.label}: ${query.isError ? 'could not be counted' : typeof total === 'number' ? total.toLocaleString() : 'loading'}`}
      className="group rounded-card border border-line bg-surface p-3.5 shadow-raised transition-colors hover:border-line-strong"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-body text-ink-muted">{tile.label}</span>
        <tile.icon aria-hidden className={cn('size-4 shrink-0', TONES[tile.tone])} />
      </div>
      {query.isLoading ? (
        <Skeleton className="mt-2 h-7 w-14" />
      ) : query.isError ? (
        // Never a zero: a count that failed is not a count of nothing. The
        // tile refetches itself every minute, and still opens the list.
        <p className="mt-2 text-body text-critical-ink">Couldn’t count these</p>
      ) : (
        <p className="mt-1.5 text-2xl font-semibold tracking-tight tabular-nums text-ink">
          {(total ?? 0).toLocaleString()}
        </p>
      )}
    </Link>
  );
}

/** One search for a panel: a few rows, kept fresh, under the shared ticket root. */
function usePanel(id: string, filter: FilterNode, sort: SortSpec[], perPage: number) {
  return useQuery<ListResponse<TicketSummary>, ApiError>({
    queryKey: ticketKeys.panel(`dashboard-${id}`, { filter, sort, perPage }),
    queryFn: () => post<ListResponse<TicketSummary>>('/tickets/search', { filter, sort, per_page: perPage }),
    refetchInterval: REFRESH_MS,
  });
}

function Panel({
  title,
  description,
  icon,
  footer,
  children,
}: {
  title: string;
  description: string;
  icon?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="min-w-0 overflow-hidden">
      <div className="flex items-start gap-2 border-b border-line px-3.5 py-2.5">
        {icon}
        <div className="min-w-0">
          <h2 className="text-body font-semibold text-ink">{title}</h2>
          <p className="text-mini text-ink-subtle">{description}</p>
        </div>
      </div>
      {children}
      {footer && <div className="border-t border-line px-3.5 py-2">{footer}</div>}
    </Card>
  );
}

function PanelSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-2 p-3.5">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </div>
  );
}

function ViewAll({ to, total }: { to: string; total: number }) {
  return (
    <Link to={to} className="text-body font-medium text-accent-ink hover:underline">
      View all {total.toLocaleString()}
    </Link>
  );
}

const QUEUE_ROWS = 8;

function MyQueue({ offerUnassigned }: { offerUnassigned: boolean }) {
  const query = usePanel('mine', MINE, SLA_URGENCY_SORT, QUEUE_ROWS);
  const rows = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;

  return (
    <Panel
      title="My queue"
      description="Assigned to you, in the order it is due: breached first, then the nearest SLA deadline."
      footer={total > 0 ? <ViewAll to={`/tickets?quick=mine,open&sort=${URGENCY}`} total={total} /> : undefined}
    >
      {query.isLoading ? (
        <PanelSkeleton rows={4} />
      ) : query.isError ? (
        // A failed load is a failure, not an empty queue: "Your queue is
        // clear" is the most reassuring possible message for the worst case.
        <ErrorState title="Couldn’t load your queue" message={query.error.message} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          className="py-10"
          title="Your queue is clear"
          description="Nothing open is assigned to you."
          action={
            offerUnassigned ? (
              <Button asChild>
                <Link to="/tickets?quick=unassigned,open&sort=created_at">Pick up unassigned work</Link>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((ticket) => (
            <li key={ticket.id}>
              <Link
                to={`/tickets/${ticket.number}`}
                className="flex items-center gap-3 px-3.5 py-2 transition-colors hover:bg-sunken"
              >
                <TicketLine ticket={ticket} />
                <span className="hidden shrink-0 sm:inline-flex">
                  <PriorityPill priority={ticket.priority} />
                </span>
                <span className="hidden shrink-0 md:inline-flex">
                  <Badge tone={ticketStatusTone(ticket.status.category)} dot>
                    {ticket.status.label}
                  </Badge>
                </span>
                <span className="w-36 shrink-0 text-end">
                  <SlaDeadline ticket={ticket} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** Number, subject and who asked: what identifies a ticket at a glance. */
function TicketLine({ ticket, when = 'activity' }: { ticket: TicketSummary; when?: 'activity' | 'created' }) {
  const at = when === 'created' ? ticket.created_at : ticket.last_activity_at;
  return (
    <span className="min-w-0 flex-1">
      <span className="flex min-w-0 items-baseline gap-2">
        <span className="shrink-0 font-mono text-mini text-ink-subtle">{ticket.number}</span>
        <span dir="auto" title={ticket.subject} className="min-w-0 truncate text-body font-medium text-ink">
          {ticket.subject}
        </span>
      </span>
      <span className="mt-0.5 flex min-w-0 items-center gap-1 text-mini text-ink-subtle">
        {ticket.requester && (
          <>
            <span dir="auto" className="min-w-0 truncate">
              {ticket.requester.name}
            </span>
            <span aria-hidden>·</span>
          </>
        )}
        <span className="shrink-0" title={absoluteTime(at)}>
          {when === 'created' ? `Raised ${relativeTime(at)}` : `Updated ${relativeTime(at)}`}
        </span>
      </span>
    </span>
  );
}

const UNASSIGNED_ROWS = 6;
const OLDEST_FIRST: SortSpec[] = [{ field: 'created_at', direction: 'asc' }];

function UnassignedPanel({ canAssign }: { canAssign: boolean }) {
  const query = usePanel('unassigned', UNASSIGNED, OLDEST_FIRST, UNASSIGNED_ROWS);
  const rows = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;

  return (
    <Panel
      title="Unassigned"
      description="Open work nobody has picked up, oldest first."
      footer={total > 0 ? <ViewAll to="/tickets?quick=unassigned,open&sort=created_at" total={total} /> : undefined}
    >
      {query.isLoading ? (
        <PanelSkeleton rows={3} />
      ) : query.isError ? (
        <ErrorState title="Couldn’t load unassigned tickets" message={query.error.message} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState className="py-8" title="Nothing unassigned" description="Every open ticket you can see has an owner." />
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((ticket) => (
            <li key={ticket.id} className="flex items-center gap-2 px-3.5 py-2 transition-colors hover:bg-sunken">
              <Link to={`/tickets/${ticket.number}`} className="flex min-w-0 flex-1 items-center gap-2">
                <TicketLine ticket={ticket} when="created" />
              </Link>
              {canAssign && <AssignToMe ticket={ticket} />}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Taking a ticket, without opening it. Goes through the ordinary update — with
 * the version the row was drawn from, so a ticket somebody else took in the
 * meantime is refused rather than silently taken from them.
 */
function AssignToMe({ ticket }: { ticket: TicketSummary }) {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const me = session?.user.id;

  const assign = useMutation<unknown, ApiError>({
    mutationFn: () =>
      patch(`/tickets/${ticket.number}`, {
        assignee_id: me,
        ...(typeof ticket.version === 'number' ? { version: ticket.version } : {}),
      }),
    onSuccess: () => {
      toast.success(`#${ticket.number} is assigned to you`, ticket.subject);
      invalidateTicket(queryClient, ticket.number);
    },
    onError: (error) => {
      toast.error(error);
      // Out of date — somebody else got there first. Show what is true now.
      if (error.code === 'stale_write') invalidateTicket(queryClient, ticket.number);
    },
  });

  if (me === undefined) return null;
  return (
    <Button
      size="sm"
      variant="secondary"
      loading={assign.isPending}
      onClick={() => assign.mutate()}
      // The visible words first, so a voice command that reads the button works.
      aria-label={`Assign to me: #${ticket.number}`}
      className="shrink-0"
    >
      {!assign.isPending && <UserPlus aria-hidden className="size-3.5" />}
      Assign to me
    </Button>
  );
}

interface Workload {
  id: number;
  full_name: string;
  is_available: boolean;
  open_ticket_count: number;
  breached_count: number;
  at_risk_count: number;
}

/** Who is carrying what, for whoever balances the desk's work. */
function TeamPanel() {
  const query = useQuery<{ data: Workload[] }, ApiError>({
    queryKey: ticketKeys.workload,
    queryFn: () => get('/agents/workload'),
    refetchInterval: REFRESH_MS,
  });
  const rows = query.data?.data ?? [];

  return (
    <Panel
      title="The team"
      description="Open tickets each agent is carrying right now."
      icon={<Users aria-hidden className="mt-0.5 size-4 shrink-0 text-ink-subtle" />}
    >
      {query.isLoading ? (
        <PanelSkeleton rows={3} />
      ) : query.isError ? (
        <ErrorState title="Couldn’t load the team" message={query.error.message} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState className="py-8" title="No agents yet" description="Agents appear here once they are added under People." />
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((agent) => (
            <li key={agent.id}>
              <Link
                to={`/tickets?quick=open&filter=${encodeURIComponent(
                  JSON.stringify(and({ field: 'assignee_id', op: 'is', value: agent.id })),
                )}&sort=${URGENCY}`}
                className="flex items-center gap-2 px-3.5 py-2 transition-colors hover:bg-sunken"
              >
                <Avatar name={agent.full_name} size="xs" />
                <span dir="auto" className="min-w-0 flex-1 truncate text-body text-ink">
                  {agent.full_name}
                </span>
                {!agent.is_available && <Badge>Away</Badge>}
                {agent.breached_count > 0 && <Badge tone="critical">{agent.breached_count} breached</Badge>}
                {agent.at_risk_count > 0 && <Badge tone="caution">{agent.at_risk_count} at risk</Badge>}
                <span className="w-16 shrink-0 text-end text-body text-ink-muted tabular-nums">
                  {pluralise(agent.open_ticket_count, 'open', 'open')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
